import React, { useEffect, useState } from 'react';
import { ActionType } from '@common/enums/command-hub/command-buttons';
import CustomSyncIcon from '../../misc/CustomSyncIcon';
import { useTranslation } from 'react-i18next';
import SyncStatus from '../../../models/enums/sync-status';
import { selectRobotsData } from '../../../store/robots/robots-slice-selectors';
import { useAppDispatch, useAppSelector } from '../../../store';
import MessageType from '@common/enums/robot/rsw/message-types';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import { sendRobotWebsocketMessage } from 'store/middleware/robot-websocket-middleware';
import ConfirmAbortDialog from '@common/components/General/ConfirmAbortDialog';
import { useSharedSelector } from '@common/store/SharedProvider';
import { selectWebappHasUnsyncedChanges } from '@common/store/app-configurations/app-configurations-selectors';
import { trackEvent } from 'services/analytics';
import { AnalyticsEvent, RobotAccessAction } from 'services/analytics-events';

const DataSyncButton: React.FC<{ activeOverride?: boolean; forceDisable?: boolean }> = ({
  activeOverride,
  forceDisable,
}) => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const robotsData = useAppSelector((state) => selectRobotsData(state));
  const currentRobotId = robotsData.currentRobot?.id ?? null;
  const webappHasUnsyncedChanges = useSharedSelector((state) =>
    selectWebappHasUnsyncedChanges(state),
  );

  const [isSyncInProgress, setIsSyncInProgress] = useState(false);
  const [isWebappSyncing, setIsWebappSyncing] = useState(false);
  const [isTimeoutReached, setIsTimeoutReached] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  useEffect(() => {
    if (!webappHasUnsyncedChanges && isWebappSyncing) {
      setIsSyncInProgress(false);
      setIsTimeoutReached(false);
      setIsWebappSyncing(false);
    }
  }, [webappHasUnsyncedChanges, isWebappSyncing]);

  useEffect(() => {
    if (webappHasUnsyncedChanges && isSyncInProgress) {
      setIsWebappSyncing(true);
    }
  }, [isSyncInProgress, webappHasUnsyncedChanges]);

  useEffect(() => {
    let timer: NodeJS.Timeout;

    if (isSyncInProgress) {
      timer = setTimeout(() => {
        setIsTimeoutReached(true);
        trackEvent(AnalyticsEvent.RobotSyncTimedOut, {
          sync_type: RobotAccessAction.FullDataSync,
          robot_id: currentRobotId,
        });
        setIsSyncInProgress(false);
      }, 30000);
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [isSyncInProgress, currentRobotId]);

  if (robotsData.currentRobot == null || robotsData.currentRobot.status == null) return null;

  const openConfirmDialogClickHandler = () => {
    setIsConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    trackEvent(AnalyticsEvent.RobotAccessUsed, {
      access_action: RobotAccessAction.FullDataSyncCancelled,
      robot_id: robotsData.currentRobot?.id ?? null,
      robot_serial: robotsData.currentRobot?.serialNumber ?? null,
    });
    setIsConfirmDialogOpen(false);
  };

  const syncDataHandler = () => {
    trackEvent(AnalyticsEvent.RobotAccessUsed, {
      access_action: RobotAccessAction.FullDataSync,
      robot_id: robotsData.currentRobot?.id ?? null,
      robot_serial: robotsData.currentRobot?.serialNumber ?? null,
    });
    setIsConfirmDialogOpen(false);
    setIsSyncInProgress(true);
    dispatch(
      sendRobotWebsocketMessage({
        type: MessageType.Actions,
        message: { action: ActionType.FullDataSync },
      }),
    );
  };

  return (
    <React.Fragment>
      <Tooltip
        title={
          isTimeoutReached
            ? t('info-tooltip-robot-sync-timeout-reached')
            : !robotsData.currentRobot.status.active
              ? t('info-tooltip-robot-sync-robot-offline')
              : t('info-tooltip-robot-sync')
        }
      >
        <span>
          <Button
            disabled={isSyncInProgress || forceDisable}
            fullWidth
            variant="outlined"
            onClick={openConfirmDialogClickHandler}
            sx={(theme) => ({
              color: isTimeoutReached
                ? theme.palette.error.main
                : (!robotsData.currentRobot!.status!.active || !webappHasUnsyncedChanges) &&
                    !activeOverride
                  ? theme.palette.action.disabled
                  : theme.palette.info.main,
            })}
            startIcon={
              <CustomSyncIcon
                status={
                  isSyncInProgress
                    ? SyncStatus.IN_PROGRESS
                    : isTimeoutReached
                      ? SyncStatus.FAILED
                      : SyncStatus.SUCCEEDED
                }
              />
            }
          >
            {t('button-restore-data')}
          </Button>
        </span>
      </Tooltip>
      <ConfirmAbortDialog
        isOpen={isConfirmDialogOpen}
        title={t('title-confirm-data-sync')}
        description={t('info-confirm-data-sync')}
        abortButtonText={t('button-cancel')}
        confirmButtonText={t('button-confirm')}
        onConfirm={syncDataHandler}
        onAbort={closeConfirmDialog}
      />
    </React.Fragment>
  );
};

export default DataSyncButton;
